import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Plus, Minus } from 'lucide-react';
import ITApplicationList from './ITApplicationList';
import ITApplicationForm from './ITApplicationForm';
import { ITApplication, ITAppType } from './ITApplicationTypes';
import applicationsService, { DBApplication } from '../../services/applicationsService';
import WorkflowModal from '../common/WorkflowModal';
import { usePermission } from '../../contexts/PermissionContext';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Toast, { ToastMessage } from '../common/Toast';
import extractApiError from '../../utils/apiError';
import { confirmWarning, confirmActive } from '../common/confirm';

const RBAC_ACTIVITY = 'ORG_ITGC_APPLICATIONS';

// --- Ownership type mapping: Frontend ↔ DB ---
const ownershipToDb: Record<string, string> = {
    'IN_HOUSE': 'Self Developed',
    'PURCHASED': 'Purchased',
    'LICENSED': 'Licensed',
};
const ownershipFromDb: Record<string, ITAppType> = {
    'Self Developed': 'IN_HOUSE',
    'Purchased': 'PURCHASED',
    'Licensed': 'LICENSED',
};

// --- Mapping helpers: DB ↔ UI ---
const dbToUi = (item: DBApplication): ITApplication => ({
    id: String(item.id),
    name: item.application_name,
    code: (item as any).code || undefined,
    category: item.category || 'APPLICATION',
    parentId: item.parent_application_id ? String(item.parent_application_id) : 'root',
    type: item.ownership_type ? ownershipFromDb[item.ownership_type] : undefined,
    version: item.version_number || undefined,
    rollOutDate: item.purchase_date || undefined,
    purpose: item.description || undefined,
    vendor: item.vendor_name || undefined,
    is_active: item.is_active,
    createdAt: item.created_on,
    updatedAt: item.modified_on || undefined,
    workflow_state: item.workflow_state,
    submitted_by: item.submitted_by,
    submitted_on: item.submitted_on,
    approved_by: item.approved_by,
    approved_on: item.approved_on,
    approval_note: item.approval_note,
    rejected_by: item.rejected_by,
    rejected_on: item.rejected_on,
    rejection_reason: item.rejection_reason,
    created_by_name: (item as any).created_by_name,
    submitted_by_name: (item as any).submitted_by_name,
    approved_by_name: (item as any).approved_by_name,
    rejected_by_name: (item as any).rejected_by_name,
});

const uiToDb = (data: Omit<ITApplication, 'id'>): Partial<DBApplication> => ({
    application_name: data.name,
    description: data.purpose || undefined,
    category: data.category || 'APPLICATION',
    version_number: data.version || undefined,
    parent_application_id: data.parentId && data.parentId !== 'root' ? Number(data.parentId) : undefined,
    ownership_type: data.type ? ownershipToDb[data.type] : undefined,
    vendor_name: data.vendor || undefined,
    purchase_date: data.rollOutDate || undefined,
});

interface ITApplicationManagementProps {
    compact?: boolean;
}

const ITApplicationManagement: React.FC<ITApplicationManagementProps> = ({ compact = false }) => {
    // ── RBAC gates ────────────────────────────────────────────────────────
    const viewPerm    = usePermission(RBAC_ACTIVITY, 'view');
    const modifyPerm  = usePermission(RBAC_ACTIVITY, 'view+modify');
    const canView     = viewPerm.allowed;
    const canModify   = modifyPerm.allowed;
    const permsLoading = viewPerm.loading;

    const [applications, setApplications] = useState<ITApplication[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingApp, setEditingApp] = useState<ITApplication | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [workflowOpenFor, setWorkflowOpenFor] = useState<ITApplication | null>(null);

    const loadData = useCallback(async () => {
        if (!canView) return;
        try {
            setLoading(true);
            const data = await applicationsService.getAll({
                status: statusFilter,
                search: searchTerm || undefined,
                limit: 200,
            });
            setApplications(data.map(dbToUi));
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Failed to load applications' });
            console.error('Error loading applications:', err);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, searchTerm, canView]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const parentIds = useMemo(() => {
        const ids = new Set<string>();
        const parents = new Set(
            applications
                .map(a => a.parentId)
                .filter((p): p is string => p != null && p !== 'root'),
        );
        applications.forEach(a => { if (parents.has(a.id)) ids.add(a.id); });
        return ids;
    }, [applications]);

    const handleToggleExpand = (id: string) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const isAllExpanded = parentIds.size > 0 && expandedIds.size >= parentIds.size;

    const handleToggleAll = () => {
        if (isAllExpanded) setExpandedIds(new Set());
        else setExpandedIds(new Set(parentIds));
    };

    const handleOpenAdd = () => {
        if (!canModify) return;
        setEditingApp(null);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (app: ITApplication) => {
        setEditingApp(app);
        setIsModalOpen(true);
    };

    // ── Workflow ──────────────────────────────────────────────────────────
    // submit/approve/reject/reopen are driven end-to-end by <WorkflowModal>
    // via the /api/v1/wf engine (kind="org_application").
    const closeWfModal = () => setWorkflowOpenFor(null);

    const handleToggleActive = async () => {
        if (!editingApp) return;
        const next = editingApp.is_active ? 0 : 1;
        const confirmFn = editingApp.is_active ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this application?' : 'Inactivate this application?',
            text: next
                ? 'It will appear in active lists again.'
                : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        try {
            await applicationsService.update(Number(editingApp.id), { is_active: next });
            setEditingApp({ ...editingApp, is_active: next });
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Application Reactivated.' : 'Application Inactivated.' });
            await loadData();
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Failed to update status.' });
        }
    };

    const handleSubmit = async (data: Omit<ITApplication, 'id'>) => {
        try {
            const dbData = uiToDb(data);
            if (editingApp) {
                await applicationsService.update(Number(editingApp.id), dbData);
                setToast({ type: 'success', message: 'Application updated successfully.' });
            } else {
                await applicationsService.create(dbData);
                setToast({ type: 'success', message: 'Application created successfully.' });
            }
            setIsModalOpen(false);
            await loadData();
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Failed to save application.' });
        }
    };

    // ── Not-authorised gate ───────────────────────────────────────────────
    if (!permsLoading && !canView) {
        return (
            <div className={`netbody-card flex flex-col h-full ${compact ? '' : 'min-h-[500px]'}`}>
                <NoDataFound
                    title="Not authorised"
                    message={viewPerm.message || 'You are not authorised to view IT Applications. Please contact your Super Admin.'}
                />
            </div>
        );
    }

    return (
        <div className={`netbody-card flex flex-col h-full ${compact ? '' : 'min-h-[500px]'}`}>

            <div className="netbody-header">
                {/* Status — iOS-style on/off switch (on = active) */}
                <StatusSwitch
                    value={statusFilter === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE'}
                    onChange={next => setStatusFilter(next)}
                    noun="applications"
                    testId="SEL_it_app_status_filter"
                />
                <Button
                    variant="secondary-outline"
                    icon={isAllExpanded ? Minus : Plus}
                    onClick={handleToggleAll}
                    data-testid="BTN_it_app_mgmt_expand_all"
                >
                    {isAllExpanded ? 'Collapse All' : 'Expand All'}
                </Button>
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search applications..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_it_app_search"
                    />
                    {canModify && (
                        <Button variant="fill" icon={Plus} onClick={handleOpenAdd} data-testid="BTN_it_app_mgmt_create">
                            Create
                        </Button>
                    )}
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <ITApplicationList
                        applications={applications}
                        loading={loading}
                        onEdit={handleOpenEdit}
                        expandedIds={expandedIds}
                        onToggleExpand={handleToggleExpand}
                        onWorkflowClick={setWorkflowOpenFor}
                    />
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />
            <ITApplicationForm
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleSubmit}
                initialData={editingApp}
                applications={applications}
                onToggleActive={editingApp && canModify ? handleToggleActive : undefined}
                canModify={canModify}
            />
            <WorkflowModal
                isOpen={!!workflowOpenFor}
                onClose={closeWfModal}
                kind="org_application"
                id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
                onChanged={loadData}
            />
        </div>
    );
};

export default ITApplicationManagement;
